import * as THREE from "../../libs/three.js/build/three.module.js";
import { CSS3DRenderer, CSS3DObject } from '../../jsm/renderers/CSS3DRenderer.js';

/**
 * 把 CSS3DObject 的平面（默认在自身 XY 平面、法线 +Z）贴到 YDBoxVolume 正面的朝向修正。
 *
 * 必须与胶囊控制条一致（Volume.js createCapsuleBar：bar.rotation.y=PI/2 + bar.rotation.x=PI/2），
 * 而 THREE 的 Euler 默认序 'XYZ' 表示 R = Rx·Ry·Rz，作用到向量是先 Ry 再 Rx。
 * 注意不能写成 obj.rotateY(PI/2) 后 obj.rotateX(PI/2) —— 那是 Q·Ry·Rx，与 Q·Rx·Ry 不等。
 *
 * 验算（Rx(π/2)·Ry(π/2) 作用到平面三轴）：
 *   法线 +Z (0,0,1) → (1,0,0)  = 盒子局部 +X（正面法线）✓
 *   平面 +X (1,0,0) → (0,1,0)  = 盒子局部 +Y（正面宽度）✓
 *   平面 +Y (0,1,0) → (0,0,1)  = 盒子局部 +Z（正面高度）✓
 */
const FRONT_QUAT = new THREE.Quaternion().setFromEuler(
	new THREE.Euler(Math.PI / 2, Math.PI / 2, 0, 'XYZ')
);

/**
 * CSS3DLayer — 把真 iframe 按 3D 位姿摆进场景的 DOM 叠加层。
 *
 * 用于 YDBoxVolume 的「网页」资源(id=9)：网页是可鼠标操作的真 iframe，随相机做
 * 完整的 3D 透视跟随，而不是截图贴图。
 *
 * ─── 设计要点 ───────────────────────────────────────────────────────
 * 1. 独立 THREE.Scene，不挂进 viewer 的 WebGL 场景。
 *    CSS3DObject 也【不】作为 volume 的子对象，而是每帧从 volume.matrixWorld
 *    重新取位置/朝向。原因：volume 的 scale 是非等比的 (厚度, 宽度, 高度)，
 *    作为子对象会继承这个非等比缩放把 iframe 拉变形（PPT 胶囊条就得靠
 *    setBarScale 反算来抵消）。这里改为自己用【等比】scale + 调整 iframe 的
 *    像素宽高比，从根上避开变形。
 *
 * 2. 无法被点云/高斯遮挡。iframe 是 DOM，走浏览器合成层，不进 WebGL 管线，
 *    与点云没有共享深度缓冲。这是原理层面的限制。
 *    （对比：YDBoxVolume 的 box 是真 Mesh，在 render.pass.depth 以
 *      depthTest:true / depthWrite:false 画到默认 framebuffer，能逐像素被遮挡。）
 *
 * 3. 注册表为空时整个容器 display:none，零开销；有内容才逐帧同步。
 */
export class CSS3DLayer {

	/** iframe 的基准像素宽度。世界尺寸通过 CSS3DObject 的等比 scale 换算得到。 */
	static BASE_PX = 1280;

	constructor(viewer) {
		this.viewer = viewer;

		this.renderer = new CSS3DRenderer();
		const dom = this.renderer.domElement;
		dom.style.position = 'absolute';
		dom.style.top = '0px';
		dom.style.left = '0px';
		// iframe 放在 WebGL canvas 之下，让打洞 pass（HolePunchLayer）能把 canvas
		// 对应像素 alpha 覆写为 0 透出 iframe，实现逐像素遮挡。
		// canvas z-index=2（由 viewer 在启用网页盒时设），CSS3D dom z-index=1。
		dom.style.zIndex = '1';
		// 容器本身不吃鼠标事件，只有 iframe（CSS3DObject 构造函数里设了 pointerEvents:'auto'）吃。
		dom.style.pointerEvents = 'none';
		dom.style.display = 'none';
		viewer.renderArea.appendChild(dom);

		this.scene = new THREE.Scene();

		/** @type {Map<THREE.Object3D, {object: CSS3DObject, iframe: HTMLIFrameElement, url: string}>} */
		this.entries = new Map();

		// 网页盒交互策略：iframe 默认可交互（保留"真网页可点击"），仅在 Potree 拖拽期间
		// 统一让出鼠标。相机轨道、盒子放置、gizmo 变换都走 startDragging -> drop，
		// 这三类操作的 drag 状态会在 update() 里被轮询到，一次性解决"放不下盒子"
		// "选不中网页盒""拖不动 gizmo"等问题。
		// （select/deselect 不再用于切换交互：落位后用户应能立即操作网页，而点击盒子
		//   正面会被 iframe 挡住 -- 这是 CSS3D 在 canvas 之上的固有代价，靠点边框/侧面选中。）
		this._dragging = false

		// 复用的临时对象，避免每帧 GC（与 viewer.updateAnnotations 的做法一致）
		this._tmpPos = new THREE.Vector3();
		this._tmpQuat = new THREE.Quaternion();
		this._tmpScale = new THREE.Vector3();

		this.resize();
	}

	/** 跟随 WebGL canvas 尺寸。由 viewer.onResize 调用。 */
	resize() {
		const canvas = this.viewer.renderer.domElement;
		const width = canvas.clientWidth || canvas.width;
		const height = canvas.clientHeight || canvas.height;
		if (width > 0 && height > 0) {
			this.renderer.setSize(width, height);
		}
	}

	/**
	 * 给一个 volume 挂上网页 iframe。重复 attach 同一 volume 时替换 URL。
	 * @param {THREE.Object3D} volume 承载网页的 YDBoxVolume
	 * @param {string} url 网页地址
	 */
	attach(volume, url) {
		const existing = this.entries.get(volume);
		if (existing) {
			if (existing.url !== url) {
				existing.iframe.src = url;
				existing.url = url;
			}
			return existing.object;
		}

		const iframe = document.createElement('iframe');
		iframe.style.border = '0px';
		iframe.style.background = '#ffffff';
		// 沙箱：允许脚本/表单/弹窗与同源访问，但不给 top-navigation，避免嵌入页把整个应用导航掉
		iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups');
		iframe.setAttribute('referrerpolicy', 'no-referrer');
		iframe.setAttribute('allowfullscreen', 'false');
		iframe.src = url;

		const object = new CSS3DObject(iframe);
		this.scene.add(object);

		const entry = { object, iframe, url };
		this.entries.set(volume, entry);
		this.renderer.domElement.style.display = '';
		// canvas 提到 iframe 之上（z-index:2 > CSS3D dom 的 1），让打洞 pass 写 alpha=0
		// 时能透出下层 iframe；mousemove 时由 HolePunchLayer 按像素 alpha 切 canvas.pointerEvents
		const canvas = this.viewer.renderer.domElement;
		if (!canvas.style.zIndex) canvas.style.zIndex = '2';
		// 立即做一次同步，避免首帧出现在原点
		this._syncEntry(volume, entry);
		// 联动打洞层：注册一个与 iframe 同位姿的透明 quad，参与逐像素遮挡
		this.viewer.getHolePunchLayer()?.attach(volume);
		return object;
	}

	/** 摘掉某个 volume 的网页 iframe 并释放 DOM。幂等。 */
	detach(volume) {
		const entry = this.entries.get(volume);
		if (!entry) return;
		this.entries.delete(volume);
		this.scene.remove(entry.object);
		// CSS3DRenderer 把 element 挂在自己的 cameraElement 下，需手动摘掉
		if (entry.iframe.parentNode) {
			entry.iframe.parentNode.removeChild(entry.iframe);
		}
		// 断开加载中的请求
		entry.iframe.src = 'about:blank';
		// 联动打洞层：摘掉对应的打洞 quad
		this.viewer.getHolePunchLayer()?.detach(volume);
		if (this.entries.size === 0) {
			this.renderer.domElement.style.display = 'none';
			// 无网页盒时恢复 canvas 正常层叠与鼠标穿透
			this.viewer.renderer.domElement.style.zIndex = '';
			this.viewer.renderer.domElement.style.pointerEvents = '';
		}
	}

	/** 每帧调用：同步全部 iframe 的 3D 位姿并渲染 DOM 层。 */
	update(camera) {
		if (this.entries.size === 0) return

		// iframe 交互策略：
		//  1) Potree 拖拽中（inputHandler.drag 非空）：iframe 让出鼠标（相机轨道/盒子放置/gizmo 变换）
		//  2) volume.webInteractive === false：业务主动关闭网页交互（纯展示模式）
		// 注意：选中态不再让 iframe 失去交互。选中态下 gizmo 手柄在盒子周围，
		// 而网页盒正面被 iframe 覆盖，用户需要操作网页时不应被 gizmo 阻挡。
		// gizmo 手柄的射线拾取由 InputHandler 优先处理（interactiveScene 注册在前），
		// iframe 只在没有命中手柄时才接收事件。
		// canvas 的 pointerEvents 由 HolePunchLayer 按鼠标位置控制（射线检测盒子正面范围）。
		const dragging = !!this.viewer.inputHandler && !!this.viewer.inputHandler.drag
		for (const [volume, entry] of this.entries) {
			let pe = 'auto'
			// 隐藏的网页盒必须让出鼠标：iframe 的 DOM 元素虽然会被 CSS3DRenderer 设成
			// display:none（见 _syncEntry 末尾的 obj.visible 同步），但 pointerEvents
			// 留在 'auto' 时若渲染时序上尚未生效，仍可能吃掉一次事件。显式关掉更稳。
			if (volume.visible === false) {
				pe = 'none'
			} else if (dragging) {
				pe = 'none'
			} else if (volume.webInteractive === false) {
				pe = 'none'
			}
			if (pe !== entry._lastPE) {
				entry._lastPE = pe
				entry.iframe.style.pointerEvents = pe
			}
		}

		if (!camera) return
		for (const [volume, entry] of this.entries) {
			this._syncEntry(volume, entry)
		}
		this.renderer.render(this.scene, camera)
	}

	/**
	 * 把一个 entry 的 CSS3DObject 对齐到 volume 正面。
	 *
	 * YDBoxVolume 的局部坐标约定（见 Volume.js createGeometry / createCapsuleBar）：
	 *   +X 是正面法线，scale = (厚度, 宽度, 高度)，正面在 x=+0.5 平面上，
	 *   面内宽度沿 local Y、高度沿 local Z。
	 * 贴面朝向修正见文件头 FRONT_QUAT。
	 */
	_syncEntry(volume, entry) {
		volume.updateMatrixWorld();

		const worldWidth = Math.abs(volume.scale.y) || 1;
		const worldHeight = Math.abs(volume.scale.z) || 1;

		// iframe 像素尺寸：宽度固定基准，高度按世界宽高比换算 → 保证不变形
		const basePx = CSS3DLayer.BASE_PX;
		const pxW = basePx;
		const pxH = Math.max(1, Math.round(basePx * worldHeight / worldWidth));
		const style = entry.iframe.style;
		if (style.width !== pxW + 'px') style.width = pxW + 'px';
		if (style.height !== pxH + 'px') style.height = pxH + 'px';

		// 位姿：从 volume 世界矩阵里取平移与旋转，丢掉非等比 scale
		volume.matrixWorld.decompose(this._tmpPos, this._tmpQuat, this._tmpScale);

		const obj = entry.object;
		obj.position.copy(this._tmpPos);
		// 贴到正面：与胶囊条同一套朝向修正（Q·Rx·Ry，见 FRONT_QUAT 注释）
		obj.quaternion.copy(this._tmpQuat).multiply(FRONT_QUAT);
		// 此时 obj 的局部 +Z 就是盒子的正面法线，沿它外移半个厚度 + 一点偏置，
		// 压住盒面贴图避免共面导致的观感抖动
		const offset = Math.abs(volume.scale.x) * 0.5 + worldWidth * 0.002;
		obj.translateZ(offset);

		// 等比 scale：世界宽度 / 像素宽度
		const s = worldWidth / pxW;
		obj.scale.set(s, s, s);
		// 显隐同步：CSS3DObject 按设计不是 volume 的子对象（见文件头设计要点 1，
		// 避免继承非等比 scale 把 iframe 拉变形），所以父链的 visible 传不过来，
		// 必须显式同步 —— 否则 volume.visible=false 之后 iframe 仍悬浮在原位、
		// 仍跟随相机、仍可点击。CSS3DRenderer.render 内部对 visible===false 的对象
		// 会递归把子树的 element.style.display 设为 'none'，这里只需把值传给它。
		obj.visible = volume.visible !== false;
		obj.updateMatrixWorld();
	}

	/**
	 * 网页可交互 / 可拖动的互斥控制。
	 * iframe 会吞掉覆盖区域的鼠标事件，导致 Potree 无法拾取/拖动这个盒子。
	 * 该盒被选中（进入变换态）时置 none 让出鼠标，取消选中后恢复 auto。
	 */
	setInteractive(volume, interactive) {
		const entry = this.entries.get(volume);
		if (!entry) return;
		entry.iframe.style.pointerEvents = interactive ? 'auto' : 'none';
	}

	/** 释放整个层（viewer 销毁时调用）。 */
	dispose() {
		for (const volume of Array.from(this.entries.keys())) {
			this.detach(volume);
		}
		const dom = this.renderer.domElement;
		if (dom.parentNode) dom.parentNode.removeChild(dom);
	}
}
